import React, { useEffect, useState, useCallback } from 'react';
import usePersistedState from '../usePersistedState';
import { registerShortcut } from '../shortcuts';
import toast from 'react-hot-toast';

const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
const api = async (method, path, body) => {
  const m = (await import('axios')).default.create({ baseURL: API_BASE });
  if (method === 'get') return m.get(path);
  if (method === 'post') return m.post(path, body);
  if (method === 'delete_mc') return m.delete(path);
  return m.put(path, body);
};

export default function TrialMatrix({ productId, versions, onRefresh }) {
  const [data, setData] = useState(null);
  const dataRef = React.useRef(null);
  const cellIdsRef = React.useRef({});
  const [saving, setSaving] = useState({});
  const [showAddRM, setShowAddRM] = useState(false);
  const [showAddTrial, setShowAddTrial] = useState(false);
  const [newRM, setNewRM] = useState({ item_code: '', item_name: '', unit: 'g' });
  const [newTrial, setNewTrial] = useState({ version_id: '', trial_number: '', date: new Date().toISOString().split('T')[0] });
  const [itemSearch, setItemSearch] = useState('');
  const [pendingChanges, setPendingChanges] = usePersistedState('matrix.' + productId + '.pending', {});
  const [localValues, setLocalValues] = usePersistedState('matrix.' + productId + '.values', {});   // key: itemCode-trialId, val: qty string
  const [localUnits, setLocalUnits] = usePersistedState('matrix.' + productId + '.units', {});     // key: itemCode, val: unit string
  const [localComments, setLocalComments] = usePersistedState('matrix.' + productId + '.comments', {});  // key: trialId, val: comment string
  const [isSaving, setIsSaving] = useState(false);
  const hasRestored = Object.keys(localValues).length > 0 || Object.keys(localComments).length > 0 || Object.keys(localUnits).length > 0;
  useEffect(() => {
    if (hasRestored) sessionStorage.setItem('nps:lastMatrix', JSON.stringify({ productId }));
    // eslint-disable-next-line
  }, [hasRestored, productId]);

  const [itemResults, setItemResults] = useState([]);
  const [editingRM, setEditingRM] = useState(null); // item_code being edited
  const [editRMForm, setEditRMForm] = useState({ item_code: '', item_name: '' });
  const [editItemSearch, setEditItemSearch] = useState('');
  const [editItemResults, setEditItemResults] = useState([]);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [movingTrial, setMovingTrial] = useState(null);
  const [moveProductSearch, setMoveProductSearch] = useState('');
  const [moveProductResults, setMoveProductResults] = useState([]);

  const load = useCallback(() => {
    api('get', `/products/${productId}/trial-matrix`).then(r => {
      setData(r.data);
      dataRef.current = r.data;
      const vals = {};
      const units = {};
      const { cells, rms } = r.data;
      rms.forEach(rm => { units[rm.item_code] = rm.unit || 'kg'; });
      Object.entries(cells).forEach(([itemCode, trialMap]) => {
        Object.entries(trialMap).forEach(([trialId, cell]) => {
          vals[`${itemCode}-${trialId}`] = cell.qty != null ? String(cell.qty) : '';
          cellIdsRef.current[`${itemCode}-${trialId}`] = cell.id;
          if (cell.unit) units[itemCode] = cell.unit;
        });
      });
      // Merge: server values as base, any restored unsaved edits win
      setLocalValues(v => ({ ...vals, ...v }));
      setLocalUnits(u => ({ ...units, ...u }));
      setPendingChanges(p => (Object.keys(p).length ? p : {}));
      const comments = {};
      r.data.trials.forEach(t => { comments[t.id] = t.notes || ''; });
      setLocalComments(cm => ({ ...comments, ...cm }));
    });
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!newTrial.version_id && versions?.length) setNewTrial(f => ({ ...f, version_id: versions[versions.length - 1]?.id || '' }));
  }, [versions, newTrial.version_id]);

  const searchItems = async (q) => {
    setItemSearch(q);
    if (q.length < 2) { setItemResults([]); return; }
    const r = await api('get', `/items?search=${encodeURIComponent(q)}&classification=${encodeURIComponent('RAW MATERIALS,INGREDIENTS')}`);
    setItemResults(r.data.slice(0, 8));
  };

  const handleQtyChange = (itemCode, trialId, value) => {
    const key = `${itemCode}-${trialId}`;
    setLocalValues(v => ({ ...v, [key]: value }));
    const existingId = cellIdsRef.current[`${itemCode}-${trialId}`];
    setPendingChanges(p => ({
      ...p,
      [key]: { itemCode, trialId, qty: value, unit: localUnits[itemCode] || 'kg', existingId: existingId }
    }));
  };

  const handleUnitChange = (itemCode, value) => {
    setLocalUnits(u => ({ ...u, [itemCode]: value }));
    const { trials, cells } = dataRef.current || data;
    trials.forEach(t => {
      const key = `${itemCode}-${t.id}`;
      const cellId = cellIdsRef.current[key];
      const qty = localValues[key] ?? (cells[itemCode]?.[t.id]?.qty != null ? String(cells[itemCode][t.id].qty) : "");
      if (qty !== "" || cellId) {
        setPendingChanges(p => ({
          ...p,
          [key]: { itemCode, trialId: t.id, qty, unit: value, existingId: cellId }
        }));
      }
    });
  };

  useEffect(() => {
    return registerShortcut('save', () => saveAll());
    // eslint-disable-next-line
  });

  const [confirmTrial, setConfirmTrial] = useState(null);
  const [recipeForm, setRecipeForm] = useState({ batch_size: '', batch_unit: 'kg', fg_weight: '' });
  const [creatingRecipe, setCreatingRecipe] = useState(false);

  const trialTotalG = (t) => rms.reduce((s, rm) => {
    const key = `${rm.item_code}-${t.id}`;
    const qty = parseFloat(localValues[key]) || parseFloat(cells[rm.item_code]?.[t.id]?.qty) || 0;
    const u = (localUnits[rm.item_code] || cells[rm.item_code]?.[t.id]?.unit || rm.unit || 'g').toLowerCase();
    return s + (['kg','kgs'].includes(u) ? qty*1000 : qty);
  }, 0);

  const createRecipeFromTrial = async () => {
    const t = confirmTrial;
    const batchG = (parseFloat(recipeForm.batch_size)||0) * (recipeForm.batch_unit === 'kg' ? 1000 : 1);
    const fgG = parseFloat(recipeForm.fg_weight)||0;
    const inputG = trialTotalG(t);
    if (!(batchG > 0)) return toast.error('Recipe batch size required');
    if (!(fgG > 0)) return toast.error('Trial FG weight required');
    if (!(inputG > 0)) return toast.error('Trial has no quantities');
    setCreatingRecipe(true);
    try {
      const scale = batchG / fgG;
      const yieldPct = (fgG / inputG) * 100;
      const userName = (() => { try { const u = JSON.parse(localStorage.getItem('npd_user')); return u?.name || u?.username || ''; } catch { return ''; } })();
      const ingredients = rms.map(rm => {
        const key = `${rm.item_code}-${t.id}`;
        const qty = parseFloat(localValues[key]) || parseFloat(cells[rm.item_code]?.[t.id]?.qty) || 0;
        const u = (localUnits[rm.item_code] || cells[rm.item_code]?.[t.id]?.unit || rm.unit || 'g').toLowerCase();
        const qtyG = (['kg','kgs'].includes(u) ? qty*1000 : qty);
        return { item_code: rm.item_code, item_name: rm.item_name, quantity: Math.round(qtyG * scale) / 1000, unit: 'kg', unit_cost: 0, total_cost: 0 };
      }).filter(i => i.quantity > 0);
      const API2 = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
      API2.interceptors.request.use(cfg => { try { const u = JSON.parse(localStorage.getItem('npd_user')); if (u?.token) cfg.headers.Authorization = 'Bearer ' + u.token; } catch {} return cfg; });
      await API2.post('/products/' + productId + '/recipe', {
        trial_id: t.id, confirmed_by: userName,
        batch_size: parseFloat(recipeForm.batch_size), batch_unit: recipeForm.batch_unit,
        notes: 'Created from Trial #' + t.trial_number + ' — trial input ' + inputG.toFixed(3) + 'g, FG ' + fgG.toFixed(3) + 'g, yield ' + yieldPct.toFixed(1) + '%, scaled x' + scale.toFixed(2),
        ingredients, has_brine: false, brineIngredients: []
      });
      toast.success('Recipe created (yield ' + yieldPct.toFixed(1) + '%)');
      setConfirmTrial(null);
      onRefresh && onRefresh();
    } catch (e) { toast.error(e.response?.data?.error || 'Recipe creation failed'); }
    setCreatingRecipe(false);
  };

  const clearEditBuffers = () => {
    setLocalValues({}); setLocalUnits({}); setLocalComments({}); setPendingChanges({});
    sessionStorage.removeItem('nps:lastMatrix');
  };

  const saveAll = async () => {
    if (Object.keys(pendingChanges).length === 0) return toast.success('No changes to save');
    setIsSaving(true);
    try {
      for (const [, change] of Object.entries(pendingChanges)) {
        if (change.type === 'notes') {
          await api('put', `/trials/${change.trialId}`, { notes: change.notes, trial_number: change.trial_number, date: change.date, status: change.status });
        } else {
          await saveCell(change.itemCode, change.trialId, change.qty, change.unit, change.existingId);
        }
      }
      clearEditBuffers();
      toast.success('All changes saved');
      load();
      onRefresh && onRefresh();
    } catch(e) { toast.error('Some saves failed'); }
    setIsSaving(false);
  };

  const saveCell = async (itemCode, trialId, qty, unit, existingId) => {
    const key = `${itemCode}-${trialId}`;
    setSaving(s => ({ ...s, [key]: true }));
    try {
      const qtyNum = parseFloat(qty);
      if (!qty || isNaN(qtyNum) || qtyNum <= 0) {
        if (existingId) await api('delete_mc', `/materials/${existingId}`, {});
      } else if (existingId) {
        const existingCell = (dataRef.current || data)?.cells?.[itemCode]?.[trialId];
        const rmData = (dataRef.current || data)?.rms?.find(r => r.item_code === itemCode);
        const existingUnitCost = existingCell?.unit_cost > 0 ? existingCell.unit_cost : (rmData?.unit_cost || 0);
        await api('put', `/materials/${existingId}`, { quantity: qtyNum, unit, unit_cost: existingUnitCost });
      } else {
        const rmInfo = (dataRef.current || data)?.rms?.find(r => r.item_code === itemCode);
        const newUnitCost = rmInfo?.unit_cost || 0;
        const newTotalCost = ['g','gm','gram','grams','ml'].includes(unit.toLowerCase()) ? (qtyNum/1000)*newUnitCost : qtyNum*newUnitCost;
        await api('post', `/trials/${trialId}/materials`, {
          item_code: itemCode,
          item_name: rmInfo?.item_name || '',
          quantity: qtyNum, unit, unit_cost: newUnitCost, total_cost: newTotalCost
        });
      }
    } catch(e) { toast.error('Save failed'); }
    setSaving(s => ({ ...s, [key]: false }));
  };

  const addTrial = async () => {
    if (!newTrial.version_id || !newTrial.trial_number) return toast.error('Version and trial number required');
    try {
      await api('post', `/versions/${newTrial.version_id}/trials`, { trial_number: newTrial.trial_number, date: newTrial.date, status: 'ongoing', notes: '' });
      toast.success('Trial added');
      setShowAddTrial(false);
      setNewTrial(f => ({ ...f, trial_number: '' }));
      load();
      onRefresh && onRefresh();
    } catch(e) { toast.error('Failed to add trial'); }
  };

  const addRM = async () => {
    if (!newRM.item_code || !newRM.item_name) return toast.error('Item code and name required');
    toast.success('RM added to matrix — enter quantities per trial');
    setData(d => ({
      ...d,
      rms: [...d.rms, { item_code: newRM.item_code, item_name: newRM.item_name, unit: newRM.unit }],
    }));
    setLocalUnits(u => ({ ...u, [newRM.item_code]: newRM.unit }));
    setNewRM({ item_code: '', item_name: '', unit: 'kg' });
    setItemSearch('');
    setItemResults([]);
    setShowAddRM(false);
  };

  const searchEditItems = async (q) => {
    setEditItemSearch(q);
    if (q.length < 2) { setEditItemResults([]); return; }
    const r = await api('get', `/items?search=${encodeURIComponent(q)}&classification=${encodeURIComponent('RAW MATERIALS,INGREDIENTS')}`);
    setEditItemResults((r.data || []).slice(0, 8));
  };

  const saveEditRM = async (originalItemCode) => {
    if (!editRMForm.item_code || !editRMForm.item_name) return toast.error('Item code and name required');
    const currentData = dataRef.current || data;
    if (!currentData) return toast.error('Data not loaded');
    const cells = currentData.cells || {};
    const trialIds = (currentData.trials || []).map(t => t.id);
    let updated = 0;
    for (const tid of trialIds) {
      const cell = cells[originalItemCode]?.[tid];
      if (cell?.id) {
        await api('put', `/materials/${cell.id}`, { quantity: cell.qty, unit: localUnits[originalItemCode] || cell.unit || 'g', unit_cost: cell.unit_cost || 0, item_code: editRMForm.item_code, item_name: editRMForm.item_name });
        updated++;
      }
    }
    setEditingRM(null);
    toast.success(`RM updated (${updated} rows)`);
    load();
  };

  const searchMoveProducts = async (q) => {
    setMoveProductSearch(q);
    if (q.length < 2) { setMoveProductResults([]); return; }
    const r = await api('get', `/products?search=${encodeURIComponent(q)}`);
    setMoveProductResults((r.data || []).slice(0, 8));
  };

  const moveTrial = async (targetProductId) => {
    if (!movingTrial) return;
    try {
      await api('put', `/trials/${movingTrial}/move`, { product_id: targetProductId });
      toast.success('Trial moved');
      setShowMoveModal(false);
      setMovingTrial(null);
      load();
      onRefresh && onRefresh();
    } catch(e) { toast.error('Failed to move trial'); }
  };

  if (!data) return <div style={{ padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>Loading matrix...</div>;

  const { trials, rms, cells } = data;

  return (
    <div>
      {hasRestored && Object.keys(pendingChanges).length > 0 && (
        <div style={{ background: '#fff8e1', border: '1px solid #ffcc80', borderRadius: 8, padding: '8px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
          <span>⏱ Unsaved changes restored from your last visit — save or discard them.</span>
          <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }}
            onClick={() => { if (window.confirm('Discard the restored unsaved changes?')) clearEditBuffers(); }}>Discard</button>
        </div>
      )}
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent)' }}>Trial Matrix</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {Object.keys(pendingChanges).length > 0 && (
            <button className="btn btn-primary btn-sm" onClick={saveAll} disabled={isSaving}>
              {isSaving ? 'Saving...' : `Save (${Object.keys(pendingChanges).length})`}
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => setShowAddTrial(true)}>+ Add Trial</button>
        </div>
      </div>

      {/* Add Trial Modal */}
      {showAddTrial && (
        <div className="modal-overlay" onClick={() => setShowAddTrial(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <div className="modal-header">
              <div className="modal-title">Add Trial</div>
              <button className="modal-close" onClick={() => setShowAddTrial(false)}>×</button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Version</label>
                <select value={newTrial.version_id} onChange={e => setNewTrial(f => ({ ...f, version_id: e.target.value }))}>
                  {versions?.map(v => <option key={v.id} value={v.id}>{v.version_number}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Trial Number</label>
                <input type="number" placeholder="e.g. 4" value={newTrial.trial_number} onChange={e => setNewTrial(f => ({ ...f, trial_number: e.target.value }))} />
              </div>
              <div className="form-group full">
                <label>Date</label>
                <input type="date" value={newTrial.date} onChange={e => setNewTrial(f => ({ ...f, date: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={addTrial}>Add</button>
              <button className="btn btn-secondary" onClick={() => setShowAddTrial(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Matrix Table */}
      <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, overflow: 'visible' }}>
          <thead>
            <tr style={{ background: 'var(--bg-elevated)' }}>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, whiteSpace: 'nowrap', minWidth: 90 }}>RM CODE</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, minWidth: 160 }}>RM NAME</th>
              <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, width: 60 }}>UNIT</th>
              {trials.map(t => (
                <th key={t.id} style={{ padding: '8px 6px', textAlign: 'center', fontWeight: 600, fontSize: 11, minWidth: 80 }}>
                  <div style={{ color: 'var(--accent)' }}>T-{String(t.trial_number).padStart(2,'0')}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 10, fontWeight: 400 }}>{t.date ? new Date(t.date).toLocaleDateString('en-AE', { day:'numeric', month:'short' }) : t.version_number}</div>
                  <button onClick={() => { setMovingTrial(t.id); setShowMoveModal(true); }} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:10, padding:'1px' }} title="Move to product">↗</button>
                  <button onClick={()=>{if(window.confirm("Delete Trial #"+t.trial_number+"?"))api("delete_mc",`/trials/${t.id}/soft`).then(()=>load());}} style={{background:"none",border:"none",cursor:"pointer",color:"var(--text-muted)",fontSize:11,padding:"2px"}} title="Delete">🗑</button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rms.length === 0 && (
              <tr><td colSpan={3 + trials.length} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>No materials yet. Add a trial with materials first, or add RM below.</td></tr>
            )}
            {rms.map((rm, rmIdx) => (
              <tr key={rm.item_code+'-'+rmIdx} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>
                  {editingRM === rm.item_code+'-'+rmIdx ? (
                    <div style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>{editRMForm.item_code || '—'}</div>
                  ) : (
                    <span style={{ display:'flex', alignItems:'center', gap:4 }}>
                      <span>{rm.item_code}</span>
                      <button onClick={() => { setEditingRM(rm.item_code+'-'+rmIdx); setEditRMForm({ item_code: rm.item_code, item_name: rm.item_name }); setEditItemSearch(''); setEditItemResults([]); }}
                        style={{ background:'var(--accent)', color:'white', border:'none', borderRadius:4, cursor:'pointer', fontSize:9, padding:'1px 5px', fontWeight:700 }}>EDIT</button>
                    </span>
                  )}
                </td>
                <td style={{ padding: '6px 10px', fontWeight: 500 }}>
                  {editingRM === rm.item_code+'-'+rmIdx ? (
                    <div style={{ position: 'relative' }}>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 2 }}>
                        <input placeholder="Search item..." value={editItemSearch} onChange={e => searchEditItems(e.target.value)}
                          style={{ flex: 1, fontSize: 11, padding: '3px 6px', border: '1px solid var(--accent)', borderRadius: 4 }} autoFocus />
                        <button className="btn btn-primary btn-sm" style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => { saveEditRM(rm.item_code); setEditItemSearch(''); setEditItemResults([]); }}>✓</button>
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '2px 6px' }} onClick={() => { setEditingRM(null); setEditItemSearch(''); setEditItemResults([]); }}>✕</button>
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{editRMForm.item_name}</div>
                      {editItemResults.length > 0 && (
                        <div style={{ position: 'fixed', background: 'white', border: '1px solid var(--border)', borderRadius: 6, zIndex: 9999, minWidth: 300, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                          {editItemResults.map(item => (
                            <div key={item.item_code} onClick={() => { setEditRMForm({ item_code: item.item_code, item_name: item.item_name }); setEditItemSearch(item.item_code + ' — ' + item.item_name); setEditItemResults([]); }}
                              style={{ padding: '7px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 12 }}
                              onMouseEnter={e => e.currentTarget.style.background='var(--bg-elevated)'}
                              onMouseLeave={e => e.currentTarget.style.background='white'}>
                              <span style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>{item.item_code}</span> — {item.item_name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : rm.item_name}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                  <select
                    style={{ fontSize: 11, padding: '2px 4px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--bg-elevated)', width: 50 }}
                    value={localUnits[rm.item_code] || rm.unit || 'kg'}
                    onChange={e => handleUnitChange(rm.item_code, e.target.value)}
                  >
                    {['kg','g','L','ml','pcs','nos'].map(u => <option key={u}>{u}</option>)}
                  </select>
                </td>
                {trials.map(t => {
                  const cell = cells[rm.item_code]?.[t.id];
                  const key = `${rm.item_code}-${t.id}`;
                  const isPending = !!pendingChanges[key];
                  return (
                    <td key={t.id} style={{ padding: '4px 6px', textAlign: 'center' }}>
                      <input
                        type="number"
                        value={(localValues[key] !== undefined && localValues[key] !== '') ? localValues[key] : (cell?.qty != null ? String(cell.qty) : '')}
                        placeholder="—"
                        style={{
                          width: 64, textAlign: 'center', fontSize: 12, padding: '3px 5px',
                          border: isPending ? '1.5px solid var(--accent)' : '1px solid var(--border)',
                          borderRadius: 4,
                          background: saving[key] ? 'var(--accent-dim)' : isPending ? 'var(--accent-dim)' : 'white',
                          color: 'var(--text-primary)'
                        }}
                        onChange={e => handleQtyChange(rm.item_code, t.id, e.target.value)}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
            {/* Total row */}
            {trials.length > 0 && (
              <tr style={{ borderTop: '2px solid var(--accent)', background: 'var(--accent-dim)' }}>
                <td colSpan={3} style={{ padding: '8px 10px', fontWeight: 700, color: 'var(--accent)', fontSize: 12 }}>TOTAL (g)</td>
                {trials.map(t => {
                  const totalG = rms.reduce((s, rm) => {
                    const key = `${rm.item_code}-${t.id}`;
                    const qty = parseFloat(localValues[key]) || parseFloat(cells[rm.item_code]?.[t.id]?.qty) || 0;
                    const u = (localUnits[rm.item_code] || cells[rm.item_code]?.[t.id]?.unit || rm.unit || 'g').toLowerCase();
                    return s + (['kg','kgs'].includes(u) ? qty*1000 : qty);
                  }, 0);
                  return <td key={t.id} style={{ padding: '8px 6px', textAlign: 'center', fontWeight: 700, color: 'var(--accent)', fontSize: 13 }}>{totalG.toFixed(3)}</td>;
                })}
              </tr>
            )}
            {/* Comments row */}
            {trials.length > 0 && (
              <tr style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
                <td colSpan={3} style={{ padding: '8px 10px', fontWeight: 700, color: 'var(--text-muted)', fontSize: 11 }}>COMMENTS</td>
                {trials.map(t => (
                  <td key={t.id} style={{ padding: '4px 6px', textAlign: 'center' }}>
                    <textarea
                      value={localComments[t.id] || ''}
                      placeholder="Notes..."
                      rows={2}
                      style={{ width: 90, fontSize: 11, padding: '3px 5px', border: '1px solid var(--border)', borderRadius: 4, resize: 'vertical', fontFamily: 'inherit' }}
                      onChange={e => { setLocalComments(c => ({ ...c, [t.id]: e.target.value })); setPendingChanges(p => ({ ...p, ['notes-'+t.id]: { type: 'notes', trialId: t.id, notes: e.target.value, trial_number: t.trial_number, date: t.date, status: t.status } })); }}
                    />
                  </td>
                ))}
              </tr>
            )}
            {/* Confirm as Recipe row */}
            {trials.length > 0 && (
              <tr style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
                <td colSpan={3} style={{ padding: '8px 10px', fontWeight: 700, color: 'var(--text-muted)', fontSize: 11 }}>CONFIRM RECIPE</td>
                {trials.map(t => (
                  <td key={t.id} style={{ padding: '6px', textAlign: 'center' }}>
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ fontSize: 10, padding: '3px 8px' }}
                      onClick={() => { setConfirmTrial(t); setRecipeForm({ batch_size: '', batch_unit: 'kg', fg_weight: '' }); }}
                    >✓ Recipe</button>
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Move Trial Modal */}
      {showMoveModal && (
        <div className="modal-overlay" onClick={() => setShowMoveModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <div className="modal-title">Move Trial to Another Product</div>
              <button className="modal-close" onClick={() => setShowMoveModal(false)}>×</button>
            </div>
            <div style={{ padding: '12px 0' }}>
              <input placeholder="Search product..." value={moveProductSearch} onChange={e => searchMoveProducts(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, marginBottom: 8 }} />
              {moveProductResults.map(p => (
                <div key={p.id} onClick={() => moveTrial(p.id)}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 12 }}
                  onMouseEnter={e => e.currentTarget.style.background='var(--bg-elevated)'}
                  onMouseLeave={e => e.currentTarget.style.background='white'}>
                  <span style={{ fontFamily: 'monospace', color: 'var(--accent)', fontSize: 11 }}>{p.product_code}</span> — {p.name}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add RM */}
      <div style={{ marginTop: 12 }}>
        {!showAddRM ? (
          <button className="btn btn-secondary btn-sm" onClick={() => setShowAddRM(true)}>+ Add RM</button>
        ) : (
          <div style={{ padding: '14px', background: 'var(--bg-elevated)', borderRadius: 8, marginTop: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', marginBottom: 10 }}>Add Raw Material</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 2, minWidth: 200 }}>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>SEARCH ITEM</label>
                <div style={{ position: 'relative' }}>
                  <input placeholder="Search item code or name..." value={itemSearch} onChange={e => searchItems(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }} />
                  {itemResults.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid var(--border)', borderRadius: 8, zIndex: 100, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                      {itemResults.map(item => (
                        <div key={item.item_code} onClick={() => { setNewRM({ item_code: item.item_code, item_name: item.item_name, unit: item.unit || 'kg' }); setItemSearch(item.item_code + ' — ' + item.item_name); setItemResults([]); }}
                          style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 12 }}
                          onMouseEnter={e => e.currentTarget.style.background='var(--bg-elevated)'}
                          onMouseLeave={e => e.currentTarget.style.background='white'}>
                          <span style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>{item.item_code}</span> — {item.item_name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>UNIT</label>
                <select value={newRM.unit} onChange={e => setNewRM(f => ({ ...f, unit: e.target.value }))}
                  style={{ padding: '8px', border: '1px solid var(--border)', borderRadius: 8, width: 70, fontSize: 13 }}>
                  {['kg','g','L','ml','pcs','nos'].map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
              <button className="btn btn-primary btn-sm" onClick={addRM}>Add</button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowAddRM(false); setItemSearch(''); setItemResults([]); }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
      {confirmTrial && (() => {
        const inputG = trialTotalG(confirmTrial);
        const fgG = parseFloat(recipeForm.fg_weight) || 0;
        const batchG = (parseFloat(recipeForm.batch_size)||0) * (recipeForm.batch_unit === 'kg' ? 1000 : 1);
        const yieldPct = inputG > 0 && fgG > 0 ? (fgG / inputG) * 100 : null;
        const scale = fgG > 0 && batchG > 0 ? batchG / fgG : null;
        return (
          <div onClick={() => setConfirmTrial(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,8,18,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div onClick={e => e.stopPropagation()} style={{ width: 440, maxWidth: '94%', background: 'var(--bg-card, #fff)', borderRadius: 14, padding: 20, boxShadow: '0 16px 48px rgba(0,0,0,0.35)' }}>
              <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>Confirm Trial #{confirmTrial.trial_number} as Recipe</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Trial RM input total: <b>{inputG.toFixed(3)} g</b></div>

              <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', display: 'block', marginBottom: 4 }}>Recipe Batch Size *</label>
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                <input type="number" value={recipeForm.batch_size} onChange={e => setRecipeForm(f => ({ ...f, batch_size: e.target.value }))}
                  placeholder="e.g. 100" style={{ flex: 1, height: 38, padding: '6px 12px', border: '1.5px solid #bbb', borderRadius: 8, fontSize: 14, color: '#111', background: '#fff' }} />
                <select value={recipeForm.batch_unit} onChange={e => setRecipeForm(f => ({ ...f, batch_unit: e.target.value }))}
                  style={{ width: 80, height: 38, borderRadius: 8, border: '1.5px solid #bbb', color: '#111', background: '#fff' }}>
                  <option value="kg">kg</option><option value="g">g</option>
                </select>
              </div>

              <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', display: 'block', marginBottom: 4 }}>Trial FG Output Weight (g) *</label>
              <input type="number" value={recipeForm.fg_weight} onChange={e => setRecipeForm(f => ({ ...f, fg_weight: e.target.value }))}
                placeholder={'e.g. ' + (inputG ? (inputG * 0.9).toFixed(1) : '25')} style={{ width: '100%', height: 38, padding: '6px 12px', border: '1.5px solid #bbb', borderRadius: 8, fontSize: 14, color: '#111', background: '#fff', marginBottom: 12 }} />

              {yieldPct !== null && (
                <div style={{ fontSize: 12, background: '#f9f3f8', border: '1px solid var(--accent)', borderRadius: 8, padding: '8px 12px', marginBottom: 14 }}>
                  Yield: <b>{yieldPct.toFixed(1)}%</b>{scale !== null && <> · RM scale factor: <b>×{scale.toFixed(2)}</b> · Recipe RM total ≈ <b>{((inputG * scale)/1000).toFixed(2)} kg</b> for {(batchG/1000).toFixed(2)} kg FG</>}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setConfirmTrial(null)}>Cancel</button>
                <button className="btn btn-primary btn-sm" disabled={creatingRecipe} onClick={createRecipeFromTrial}>{creatingRecipe ? 'Creating…' : '✓ Create Recipe'}</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
